import { createFileRoute, useRouter } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () =>
    buildSeo({
      title: "Restablecer contraseña · Bizaclia",
      description: "Elige una nueva contraseña para tu cuenta de Bizaclia.",
      path: "/reset-password",
      noindex: true,
    }),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-md py-16 text-center">
      <h1 className="text-2xl font-semibold mb-2">Error</h1>
      <p className="text-muted-foreground">{error.message}</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container mx-auto max-w-md py-16 text-center">
      <h1 className="text-2xl font-semibold">No encontrado</h1>
    </div>
  ),
});

function ResetPasswordPage() {
  const router = useRouter();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Soporta tanto el flujo nuevo (?token_hash=...&type=recovery) como
    // el flujo clásico de Supabase que deja la sesión por sí mismo.
    const url = new URL(window.location.href);
    const tokenHash = url.searchParams.get("token_hash");
    const type = url.searchParams.get("type");

    if (tokenHash && type === "recovery") {
      supabase.auth
        .verifyOtp({ token_hash: tokenHash, type: "recovery" })
        .then(({ error }) => {
          if (error) {
            toast.error("El enlace ha caducado o no es válido");
            return;
          }
          setReady(true);
          // Limpia los parámetros para que el enlace no se pueda reutilizar
          window.history.replaceState({}, "", "/reset-password");
        });
      return;
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (password.length < 6) {
      toast.error("Mínimo 6 caracteres");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Contraseña actualizada");
    router.navigate({ to: "/" });
  };

  return (
    <div className="container mx-auto max-w-md py-16 px-4">
      <h1 className="text-2xl font-semibold mb-2">Nueva contraseña</h1>
      <p className="text-muted-foreground text-sm mb-6">
        Introduce tu nueva contraseña para acceder a tu cuenta.
      </p>
      {!ready ? (
        <p className="text-sm text-muted-foreground">
          Verificando el enlace de recuperación…
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">Nueva contraseña</Label>
            <PasswordInput
              id="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirmar contraseña</Label>
            <PasswordInput
              id="confirm-password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Guardando…" : "Guardar contraseña"}
          </Button>
        </form>
      )}
    </div>
  );
}
